import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { NewsfeedQueries } from "@/api/newsfeed/queries"
import {
  IArticleRaw,
  IPaginatedArticlesResponse,
  NewsfeedApiParser,
} from "@/models/newsfeed/newsfeed.parser"
import type {
  INewsfeedItem,
  IPaginatedNewsfeed,
  ArticleQueryParams,
} from "@/models/newsfeed/newsfeed.types"

export interface INewsfeedService {
  getPublishedArticles(
    params?: ArticleQueryParams,
  ): QueryResult<IPaginatedNewsfeed>
  getArticle(id: string): QueryResult<INewsfeedItem>
  getAllArticles(params?: ArticleQueryParams): QueryResult<IPaginatedNewsfeed>
  getAdminArticle(id: string): QueryResult<INewsfeedItem>
}

const EMPTY_PAGINATED_RESPONSE: IPaginatedNewsfeed = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 25,
}

function parsePaginatedResponse(
  rawData: IPaginatedArticlesResponse,
): IPaginatedNewsfeed {
  return {
    items: rawData.items.map((item) => NewsfeedApiParser.toDomain(item)),
    total: rawData.total,
    page: rawData.page,
    pageSize: rawData.pageSize,
  }
}

function parseArticle(rawData: IArticleRaw): INewsfeedItem {
  return NewsfeedApiParser.toDomain(rawData)
}

class NewsfeedServiceClass implements INewsfeedService {
  getPublishedArticles(
    params?: ArticleQueryParams,
  ): QueryResult<IPaginatedNewsfeed> {
    return useQuery({
      ...NewsfeedQueries.articles.published(params),
      parserFn: parsePaginatedResponse,
      placeholderData: EMPTY_PAGINATED_RESPONSE,
    })
  }

  getArticle(id: string): QueryResult<INewsfeedItem> {
    return useQuery({
      ...NewsfeedQueries.articles.detail(id),
      parserFn: parseArticle,
    })
  }

  getAllArticles(params?: ArticleQueryParams): QueryResult<IPaginatedNewsfeed> {
    return useQuery({
      ...NewsfeedQueries.admin.articles(params),
      parserFn: parsePaginatedResponse,
      placeholderData: EMPTY_PAGINATED_RESPONSE,
    })
  }

  getAdminArticle(id: string): QueryResult<INewsfeedItem> {
    return useQuery({
      ...NewsfeedQueries.admin.article(id),
      parserFn: parseArticle,
    })
  }
}

export const NewsfeedService = new NewsfeedServiceClass()
